import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PaymentLayout from "../layouts/PaymentLinkLayouts";
import { Building2, ChevronDown, ChevronUp, CreditCard, Download, Landmark, QrCode, Store, Wallet, AlertCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import METHOD_LOGO from "./MethodLogos";
import { getPaymentMethodDisplayName, getPaymentMethodShortName } from "../utils/paymentMethodDisplay";
import {
  detectCardBrand,
  formatCardNumber,
  formatExpiry,
  isCardBrandSupported,
  parseExpiryToYyMm,
  validateCVV,
  validateCardNumber,
  validateExpiry,
} from "../../paymentLinking/utils/cardUtils";
import darkenColor from "../utils/darkenColor";
import useTranslation from "../hooks/useTranslation";

// Placeholder untuk logo bank (sesuaikan dengan yang kamu punya)
const METHOD_LOGOS = METHOD_LOGO;
const getCanonicalMethodCode = (code) =>
  String(code || "")
    .toUpperCase()
    .replace(/_LINKQU$/, "");

// Controls which banks show first in the Virtual Account list (collapsed preview logos
// and the expanded grid) — BCA is top priority, then BNI, then BRI. Anything else keeps
// its original relative order after these three.
const VA_PRIORITY_ORDER = ["VA_BCA", "VA_BNI", "VA_BRI"];

const sortByVaPriority = (methods) =>
  [...methods].sort((a, b) => {
    const aIndex = VA_PRIORITY_ORDER.indexOf(getCanonicalMethodCode(a.code));
    const bIndex = VA_PRIORITY_ORDER.indexOf(getCanonicalMethodCode(b.code));
    const aRank = aIndex === -1 ? VA_PRIORITY_ORDER.length : aIndex;
    const bRank = bIndex === -1 ? VA_PRIORITY_ORDER.length : bIndex;
    return aRank - bRank;
  });

export default function PaymentMethod({
  paymentTo,
  onSelectMethod,
  onSubmitCardPayment,
  onSubmitHostedCard,
  paymentMethodOptions,
  onGenerateQris,
  onCheckQrisStatus,
  qrisInquiryResult,
  isQrisGenerating = false,
  isDisabled = false,
  disabledMessage = "Payment session is not ready.",
  cardPayError = null,
  onClearCardPayError,
}) {
  const { t } = useTranslation();
  const [openGroup, setOpenGroup] = useState(null);
  const [lockedMethodCode, setLockedMethodCode] = useState(null);
  const [cardForm, setCardForm] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    cardHolderName: "",
    cardEmail: "",
    cardBillingAddress: "",
  });
  const [cardErrors, setCardErrors] = useState({});
  const [hostedCard, setHostedCard] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [hostedCardErrors, setHostedCardErrors] = useState({});
  const [isCardProcessing, setIsCardProcessing] = useState(false);
  const customer = useSelector((state) => state.customer);
  const unlockTimerRef = useRef(null);
  const cardBrand = detectCardBrand(cardForm.cardNumber);

  useEffect(() => {
    setHostedCard((current) => ({
      fullName: current.fullName || customer?.fullName || "",
      email: current.email || customer?.email || "",
      phone: current.phone || customer?.phone || "",
    }));
  }, [customer?.fullName, customer?.email, customer?.phone]);
  const { secondary_color, secondary_text_color, tertiary_text_color, card_title } = useSelector(
    (state) => state.style,
  );
  const buttonTextColor = tertiary_text_color || secondary_text_color || "#FFFFFF";

  const categoryConfig = useMemo(
    () => ({
      va: {
        label: t("paymentMethod.categoryVa"),
        icon: Building2,
        aliases: ["va", "virtual_account", "bank_virtual_account"],
      },
      card: {
        label: t("paymentMethod.categoryCard"),
        icon: CreditCard,
        aliases: ["card", "credit_card", "debit_card"],
      },
      ewallet: {
        label: t("paymentMethod.categoryEwallet"),
        icon: Wallet,
        aliases: ["ewallet", "e_wallet"],
      },
      qris: {
        label: t("paymentMethod.categoryQris"),
        icon: QrCode,
        aliases: ["qris"],
      },
      offline_store: {
        label: t("paymentMethod.categoryOfflineStore"),
        icon: Store,
        aliases: ["offline_store", "retail_outlet"],
      },
      direct_debit: {
        label: t("paymentMethod.categoryDirectDebit"),
        icon: Landmark,
        aliases: ["direct_debit"],
      },
    }),
    [t],
  );

  const getCategoryKey = useCallback(
    (method) => {
      const group = String(method.group || "").toLowerCase();
      const code = String(method.code || "").toUpperCase();

      const matchedEntry = Object.entries(categoryConfig).find(([, config]) => config.aliases.includes(group));

      if (matchedEntry) {
        return matchedEntry[0];
      }

      if (code.startsWith("VA_")) return "va";
      if (code.startsWith("EWALLET_")) return "ewallet";
      if (code.includes("QRIS")) return "qris";
      if (["ALFAMART", "INDOMARET"].includes(code)) return "offline_store";
      if (code.includes("DIRECT_DEBIT")) return "direct_debit";
      if (code.includes("CARD") || code.includes("VISA") || code.includes("MASTER")) return "card";

      return "others";
    },
    [categoryConfig],
  );

  const groupedPaymentMethods = useMemo(() => {
    const dedupedMethods = paymentMethodOptions.reduce((methods, method) => {
      const canonicalCode = getCanonicalMethodCode(method.code);
      const existingIndex = methods.findIndex(
        (existingMethod) => getCanonicalMethodCode(existingMethod.code) === canonicalCode,
      );

      if (existingIndex === -1) {
        return [...methods, method];
      }

      const existingMethod = methods[existingIndex];
      const shouldPreferCurrentMethod =
        String(existingMethod.code || "").includes("_LINKQU") && !String(method.code || "").includes("_LINKQU");

      if (!shouldPreferCurrentMethod) {
        return methods;
      }

      return methods.map((existingMethodItem, index) => (index === existingIndex ? method : existingMethodItem));
    }, []);

    const groups = dedupedMethods.reduce((accumulator, method) => {
      const categoryKey = getCategoryKey(method);
      return {
        ...accumulator,
        [categoryKey]: [...(accumulator[categoryKey] || []), method],
      };
    }, {});

    const orderedKeys = ["va", "card", "ewallet", "qris", "offline_store", "direct_debit"];
    const unknownKeys = Object.keys(groups).filter((key) => !orderedKeys.includes(key));

    return [...orderedKeys, ...unknownKeys]
      .filter((key) => groups[key]?.length)
      .map((key) => ({
        key,
        label: categoryConfig[key]?.label || "Others",
        Icon: categoryConfig[key]?.icon || Wallet,
        methods: key === "va" ? sortByVaPriority(groups[key]) : groups[key],
      }));
  }, [paymentMethodOptions, categoryConfig, getCategoryKey]);

  useEffect(() => {
    return () => {
      if (unlockTimerRef.current) {
        clearTimeout(unlockTimerRef.current);
      }
    };
  }, []);

  const handleSelectMethod = (methodCode) => {
    if (isDisabled) {
      return;
    }

    if (lockedMethodCode === methodCode) {
      return;
    }

    setLockedMethodCode(methodCode);
    if (unlockTimerRef.current) {
      clearTimeout(unlockTimerRef.current);
    }
    unlockTimerRef.current = setTimeout(() => {
      setLockedMethodCode(null);
      unlockTimerRef.current = null;
    }, 5000);

    onSelectMethod(methodCode);
  };

  const handleToggleGroup = (key, methods, isOpen) => {
    const nextOpenGroup = isOpen ? null : key;
    setOpenGroup(nextOpenGroup);

    if (nextOpenGroup === "qris" && methods[0]?.code && onGenerateQris) {
      onGenerateQris(methods[0].code);
    }
  };

  const handleCardInputChange = (field, value) => {
    let nextValue = value;

    if (field === "cardNumber") {
      nextValue = formatCardNumber(value);
    } else if (field === "cardExpiry") {
      nextValue = formatExpiry(value);
    } else if (field === "cardCvv") {
      nextValue = value.replace(/\D/g, "").slice(0, cardBrand === "amex" ? 4 : 3);
    }

    setCardForm((previous) => ({
      ...previous,
      [field]: nextValue,
    }));

    if (cardErrors[field]) {
      setCardErrors((previous) => ({
        ...previous,
        [field]: null,
      }));
    }
  };

  const validateCardForm = () => {
    const errors = {};
    const detectedBrand = detectCardBrand(cardForm.cardNumber);

    if (!cardForm.cardNumber || !validateCardNumber(cardForm.cardNumber)) {
      errors.cardNumber = t("paymentMethod.errorCardNumberInvalid");
    } else if (!isCardBrandSupported(detectedBrand)) {
      errors.cardNumber = t("paymentMethod.errorCardNotSupported");
    }

    if (!cardForm.cardExpiry || !validateExpiry(cardForm.cardExpiry)) {
      errors.cardExpiry = t("paymentMethod.errorExpiryInvalid");
    }

    if (!cardForm.cardCvv || !validateCVV(cardForm.cardCvv, detectedBrand)) {
      errors.cardCvv = t(detectedBrand === "amex" ? "paymentMethod.errorCvvDigits4" : "paymentMethod.errorCvvDigits3");
    }

    if (!cardForm.cardHolderName.trim()) {
      errors.cardHolderName = t("paymentMethod.errorCardHolderRequired");
    }

    if (cardForm.cardEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardForm.cardEmail.trim())) {
      errors.cardEmail = t("paymentMethod.errorEmailInvalid");
    }

    setCardErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmitCardForm = async (event, methods) => {
    event.preventDefault();

    if (isDisabled) {
      return;
    }

    if (!validateCardForm()) {
      return;
    }

    const cardMethod = methods.find((method) => String(method.group).toLowerCase() === "card") || methods[0];

    if (!cardMethod) {
      return;
    }

    setIsCardProcessing(true);
    onClearCardPayError?.();
    try {
      await onSubmitCardPayment(cardMethod.code, {
        card_number: cardForm.cardNumber.replace(/\s/g, ""),
        card_expiry: parseExpiryToYyMm(cardForm.cardExpiry),
        card_cvv: cardForm.cardCvv,
        card_holder_name: cardForm.cardHolderName.trim(),
        card_holder_email: cardForm.cardEmail.trim() || undefined,
        customer_address: cardForm.cardBillingAddress.trim() || undefined,
      });
    } catch (_) {
      // Parent (SecondForm) sets cardPayError for API / 3DS failures.
    } finally {
      setIsCardProcessing(false);
    }
  };

  const getMethodLogos = (method) => {
    const logos = METHOD_LOGOS[String(method.code || "").toUpperCase()];

    if (!logos) {
      return [];
    }

    return Array.isArray(logos) ? logos : [logos];
  };

  const renderCategoryLogos = (methods, maxVisible = 3) => {
    const logos = methods.flatMap((method) =>
      getMethodLogos(method).map((logo) => ({
        logo,
        name: getPaymentMethodDisplayName(method),
        code: method.code,
      })),
    );
    const visibleLogos = logos.slice(0, maxVisible);
    const mobileHiddenCount = Math.max(logos.length - 1, 0);
    const desktopHiddenCount = Math.max(logos.length - visibleLogos.length, 0);

    return (
      <div className="ml-auto flex min-w-0 items-center justify-end gap-1">
        {visibleLogos.map(({ logo, name, code }, index) => (
          <div
            key={`${code}-${index}`}
            data-method-logo
            className={`${index > 0 ? "hidden sm:flex" : "hidden min-[375px]:flex"} h-9 w-14 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white px-1`}
          >
            <img
              src={logo}
              alt={`${name} logo`}
              className="h-8 w-full object-contain"
              onError={(event) => {
                event.currentTarget.closest("[data-method-logo]")?.classList.add("hidden");
              }}
            />
          </div>
        ))}
        {logos.length > 0 && (
          <span className="flex h-9 w-10 shrink-0 items-center justify-center rounded-md border border-gray-200 px-2 text-sm font-semibold text-gray-700 min-[375px]:hidden">
            +{logos.length}
          </span>
        )}
        {mobileHiddenCount > 0 && (
          <span className="hidden h-9 w-10 shrink-0 items-center justify-center rounded-md border border-gray-200 px-2 text-sm font-semibold text-gray-700 min-[375px]:flex sm:hidden">
            +{mobileHiddenCount}
          </span>
        )}
        {desktopHiddenCount > 0 && (
          <div className="hidden h-9 w-10 shrink-0 items-center justify-center rounded-md border border-gray-200 px-2 sm:flex">
            <span className="text-sm font-semibold text-gray-700">+{desktopHiddenCount}</span>
          </div>
        )}
      </div>
    );
  };

  const displayGridMethods = ["va", "ewallet", "offline_store"];
  const qrisString = qrisInquiryResult?.data?.paymentLinkHistory?.payment_method_additional || "";

  const QRIS_DOWNLOAD_SIZE = 512;

  const handleDownloadQris = () => {
    const svgElement = document.getElementById("payment-link-qris-code");

    if (!svgElement) {
      return;
    }

    const serializer = new XMLSerializer();
    const svgContent = serializer.serializeToString(svgElement);
    const svgBlob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = QRIS_DOWNLOAD_SIZE;
      canvas.height = QRIS_DOWNLOAD_SIZE;

      const context = canvas.getContext("2d");
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      URL.revokeObjectURL(svgUrl);

      canvas.toBlob(
        (jpgBlob) => {
          if (!jpgBlob) return;

          const jpgUrl = URL.createObjectURL(jpgBlob);
          const link = document.createElement("a");

          link.href = jpgUrl;
          link.download = "qris.jpg";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(jpgUrl);
        },
        "image/jpeg",
        0.95,
      );
    };
    image.src = svgUrl;
  };

  return (
    <PaymentLayout title={card_title || paymentTo}>
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">{t("paymentMethod.title")}</h3>
        {isDisabled && (
          <p className="mb-4 rounded-xl bg-gray-100 px-4 py-3 text-sm font-normal text-gray-600">{disabledMessage}</p>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {groupedPaymentMethods.map(({ key, label, Icon, methods }) => {
            const isOpen = openGroup === key;

            return (
              <div
                key={key}
                className={`border-b border-gray-200 last:border-b-0 ${isOpen ? "bg-gray-50" : "bg-white"}`}
              >
                <button
                  type="button"
                  onClick={() => handleToggleGroup(key, methods, isOpen)}
                  className="flex min-h-[76px] w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-gray-50 sm:gap-4 sm:px-5"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Icon
                      className={`h-6 w-6 shrink-0 ${isOpen ? "text-singaHijauPrimary" : "text-gray-900"}`}
                      aria-hidden
                    />
                    <span
                      className={`min-w-0 shrink font-semibold ${isOpen ? "text-singaHijauPrimary" : "text-gray-900"}`}
                    >
                      {label}
                    </span>
                  </div>
                  {isOpen ? null : renderCategoryLogos(methods, key === "ewallet" || key === "card" ? 4 : 3)}
                  {isOpen ? (
                    <ChevronUp className="h-5 w-5 shrink-0 text-gray-700" aria-hidden />
                  ) : (
                    <ChevronDown className="h-5 w-5 shrink-0 text-gray-700" aria-hidden />
                  )}
                </button>

                {isOpen && key === "qris" && (
                  <div className="bg-gray-50 px-4 pb-6 pt-2 text-center sm:px-5">
                    <p className="mx-auto max-w-[360px] text-sm font-normal leading-5 text-black">
                      {t("paymentMethod.qrisSupportNote")}
                    </p>

                    <div className="mx-auto mt-4 w-full max-w-[280px] rounded-xl bg-white px-5 py-6">
                      {isQrisGenerating && (
                        <div className="flex min-h-[240px] items-center justify-center">
                          <p className="text-sm font-normal text-gray-500">{t("paymentMethod.generatingQris")}</p>
                        </div>
                      )}

                      {!isQrisGenerating && qrisString && (
                        <div className="flex flex-col gap-4">
                          <QRCodeSVG
                            id="payment-link-qris-code"
                            value={qrisString}
                            size={240}
                            className="mx-auto h-auto w-full max-w-[240px]"
                          />
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={handleDownloadQris}
                              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border bg-singaHijauPrimary text-sm font-semibold text-white"
                            >
                              <Download className="h-5 w-5" aria-hidden />
                              {t("paymentMethod.downloadQris")}
                            </button>

                            <button
                              type="button"
                              onClick={onCheckQrisStatus}
                              className="flex h-12 w-full items-center justify-center rounded-xl border border-singaHijauPrimary bg-white text-sm font-semibold text-singaHijauPrimary transition hover:bg-green-50"
                            >
                              {t("common.checkPaymentStatus")}
                            </button>
                          </div>
                        </div>
                      )}

                      {!isQrisGenerating && !qrisString && (
                        <div className="flex min-h-[240px] items-center justify-center">
                          <p className="text-sm font-normal text-gray-500">{t("paymentMethod.qrisNotAvailable")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isOpen && key === "card" && methods.some((method) => method.card_checkout_mode === "hosted") && (
                  <form
                    className="border-gray-200 bg-gray-50 px-5 pb-6"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      if (isDisabled || isCardProcessing) return;
                      const cardMethod = methods.find((method) => String(method.group).toLowerCase() === "card");
                      if (!cardMethod) return;

                      const errors = {};
                      if (hostedCard.fullName.trim().length < 2) {
                        errors.fullName = t("paymentMethod.errorCardHolderRequired");
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hostedCard.email.trim())) {
                        errors.email = t("paymentMethod.errorEmailInvalid");
                      }
                      if (hostedCard.phone.replace(/\D/g, "").length < 8) {
                        errors.phone = t("paymentMethod.errorPhoneRequired");
                      }
                      setHostedCardErrors(errors);
                      if (Object.keys(errors).length > 0) return;

                      setIsCardProcessing(true);
                      onClearCardPayError?.();
                      try {
                        await onSubmitHostedCard(cardMethod.code, {
                          fullName: hostedCard.fullName.trim(),
                          email: hostedCard.email.trim(),
                          phone: hostedCard.phone.trim(),
                        });
                      } catch (_) {
                        setIsCardProcessing(false);
                      }
                    }}
                  >
                    <div className="grid gap-4">
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.cardholderNameLabel")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          autoComplete="name"
                          disabled={isDisabled || isCardProcessing}
                          value={hostedCard.fullName}
                          onChange={(event) => setHostedCard((current) => ({ ...current, fullName: event.target.value }))}
                          placeholder={t("paymentMethod.cardholderNamePlaceholder")}
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {hostedCardErrors.fullName && <p className="mt-1 text-sm text-red-500">{hostedCardErrors.fullName}</p>}
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.emailLabel")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          autoComplete="email"
                          disabled={isDisabled || isCardProcessing}
                          value={hostedCard.email}
                          onChange={(event) => setHostedCard((current) => ({ ...current, email: event.target.value }))}
                          placeholder={t("paymentMethod.emailPlaceholder")}
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {hostedCardErrors.email && <p className="mt-1 text-sm text-red-500">{hostedCardErrors.email}</p>}
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.phoneNumberLabel")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          autoComplete="tel"
                          disabled={isDisabled || isCardProcessing}
                          value={hostedCard.phone}
                          onChange={(event) => setHostedCard((current) => ({ ...current, phone: event.target.value }))}
                          placeholder={t("paymentMethod.phonePlaceholder")}
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {hostedCardErrors.phone && <p className="mt-1 text-sm text-red-500">{hostedCardErrors.phone}</p>}
                      </div>
                      {cardPayError && (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>{cardPayError}</span>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={isDisabled || isCardProcessing}
                        className="h-12 w-full rounded-xl text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ backgroundColor: secondary_color, color: buttonTextColor }}
                      >
                        {isCardProcessing ? t("common.processing") : t("paymentMethod.payNow")}
                      </button>
                      <button
                        type="button"
                        disabled
                        className="h-12 w-full cursor-not-allowed rounded-xl border border-singaHijauPrimary bg-white text-sm font-semibold text-singaHijauPrimary opacity-50"
                      >
                        {t("common.checkPaymentStatus")}
                      </button>
                    </div>
                  </form>
                )}

                {isOpen && key === "card" && !methods.some((method) => method.card_checkout_mode === "hosted") && (
                  <form
                    className="border-gray-200 bg-gray-50 px-5 pb-6"
                    onSubmit={(event) => handleSubmitCardForm(event, methods)}
                  >
                    <div className="grid gap-4">
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.cardNumberLabel")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="cc-number"
                          disabled={isDisabled || isCardProcessing}
                          value={cardForm.cardNumber}
                          onChange={(event) => handleCardInputChange("cardNumber", event.target.value)}
                          placeholder="1234 1234 1234 1234"
                          maxLength={19}
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {cardErrors.cardNumber && <p className="mt-1 text-sm text-red-500">{cardErrors.cardNumber}</p>}
                      </div>

                      <div className="grid grid-cols-1 gap-4 min-[450px]:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-gray-900">
                            {t("paymentMethod.cardExpiryLabel")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            disabled={isDisabled || isCardProcessing}
                            value={cardForm.cardExpiry}
                            onChange={(event) => handleCardInputChange("cardExpiry", event.target.value)}
                            placeholder="MM/YY"
                            maxLength={5}
                            className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                          />
                          {cardErrors.cardExpiry && (
                            <p className="mt-1 text-sm text-red-500">{cardErrors.cardExpiry}</p>
                          )}
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-gray-900">
                            {t("paymentMethod.cvvLabel")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            disabled={isDisabled || isCardProcessing}
                            value={cardForm.cardCvv}
                            onChange={(event) => handleCardInputChange("cardCvv", event.target.value)}
                            placeholder="123"
                            maxLength={cardBrand === "amex" ? 4 : 3}
                            className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                          />
                          {cardErrors.cardCvv && <p className="mt-1 text-sm text-red-500">{cardErrors.cardCvv}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.nameLabel")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          autoComplete="cc-name"
                          disabled={isDisabled || isCardProcessing}
                          value={cardForm.cardHolderName}
                          onChange={(event) => handleCardInputChange("cardHolderName", event.target.value)}
                          placeholder="ex: Antoni jehran"
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {cardErrors.cardHolderName && (
                          <p className="mt-1 text-sm text-red-500">{cardErrors.cardHolderName}</p>
                        )}
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.emailLabel")}
                        </label>
                        <input
                          type="email"
                          autoComplete="email"
                          disabled={isDisabled || isCardProcessing}
                          value={cardForm.cardEmail}
                          onChange={(event) => handleCardInputChange("cardEmail", event.target.value)}
                          placeholder="ex: antoni@gmail.com"
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        {cardErrors.cardEmail && <p className="mt-1 text-sm text-red-500">{cardErrors.cardEmail}</p>}
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-900">
                          {t("paymentMethod.billingAddressLabel")}
                        </label>
                        <input
                          type="text"
                          autoComplete="billing street-address"
                          disabled={isDisabled || isCardProcessing}
                          value={cardForm.cardBillingAddress}
                          onChange={(event) => handleCardInputChange("cardBillingAddress", event.target.value)}
                          placeholder="ex: Jl Medan Merdeka"
                          className="h-10.5 w-full rounded-xl border border-gray-200 bg-white px-5 text-gray-900 outline-none transition focus:border-singaHijauPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                      </div>

                      {cardPayError && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>{cardPayError}</span>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isDisabled || isCardProcessing}
                        className="w-full h-10 rounded-xl text-sm font-medium transition-colors text-white"
                        style={{
                          backgroundColor: secondary_color,
                          color: buttonTextColor,
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = darkenColor(secondary_color, 15);
                          e.target.style.color = darkenColor(buttonTextColor, 15);
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = secondary_color;
                          e.target.style.color = buttonTextColor;
                        }}
                      >
                        {isCardProcessing ? t("common.processing") : t("paymentMethod.payNow")}
                      </button>
                    </div>
                  </form>
                )}

                {isOpen && displayGridMethods.includes(key) && (
                  <div className="bg-gray-50 px-5 pb-6 pt-3">
                    <div
                      className={`grid auto-rows-fr gap-3 min-[450px]:grid-cols-2 ${key === "offline_store" ? "" : "md:grid-cols-3"}`}
                    >
                      {methods.map((method) => {
                        const displayName = getPaymentMethodDisplayName(method);
                        const shortName = getPaymentMethodShortName(method);
                        const logos = getMethodLogos(method);
                        const isLocked = lockedMethodCode === method.code;
                        const isMethodDisabled = isDisabled || isLocked;

                        return (
                          <button
                            key={method.code}
                            type="button"
                            onClick={() => handleSelectMethod(method.code)}
                            disabled={isMethodDisabled}
                            aria-busy={isLocked}
                            className={`flex flex-row items-center gap-3 rounded-xl bg-white px-4 py-6 transition-colors min-[450px]:flex-col min-[450px]:items-center min-[450px]:justify-center min-[450px]:text-center ${
                              isMethodDisabled
                                ? "cursor-not-allowed opacity-60"
                                : "hover:outline hover:outline-[#01793B]"
                            }`}
                          >
                            <div className="flex w-full items-center gap-3 min-[450px]:justify-center">
                              <div className="flex w-[94px] shrink-0 items-center justify-center">
                                {logos.length > 0 ? (
                                  <img
                                    src={logos[0]}
                                    alt={`${displayName} logo`}
                                    className={`object-contain ${
                                      key === "offline_store" ? "max-h-12 max-w-[94px]" : "max-h-10 max-w-[84px]"
                                    }`}
                                  />
                                ) : (
                                  <span className="text-sm font-medium text-gray-900">{displayName}</span>
                                )}
                              </div>

                              {logos.length > 0 && (
                                <span className="flex-1 text-left text-base text-gray-900 min-[450px]:hidden">
                                  {shortName}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PaymentLayout>
  );
}
